import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Buildings, CalendarBlank, CaretDown, ChartDonut, CurrencyCircleDollar, FileText, GearSix,
  List, Moon, Robot, SignOut, SquaresFour, Stack, Sun, X,
} from '@phosphor-icons/react';
import { api, ApiError } from './lib/api';
import type {
  Bootstrap, Company, Currency, FiscalPeriod, FiscalYear, RouteId, Theme,
} from './types';
import { Brand } from './components/Brand';
import { CustomSelect } from './components/SmartForm';
import { EmptyState, LoadingState } from './components/Ui';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { ProcessingPage } from './pages/ProcessingPage';
import { StatementsPage } from './pages/StatementsPage';
import { AiCenterPage } from './pages/AiCenterPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';

export interface WorkspaceContextValue {
  bootstrap: Bootstrap;
  companies: Company[];
  /** Null until the organisation has its first company. */
  company: Company | null;
  fiscalYears: FiscalYear[];
  fiscalYear: FiscalYear | null;
  periods: FiscalPeriod[];
  periodKey: string;
  currency: Currency;
  can: (permission: string) => boolean;
  selectCompany: (companyId: string) => void;
  selectFiscalYear: (fiscalYearId: string) => void;
  selectPeriod: (periodKey: string) => void;
  setCurrency: (currency: Currency) => void;
  refresh: () => Promise<void>;
  navigate: (route: RouteId, query?: Record<string, string>) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error('فضای کاری در دسترس نیست.');
  return value;
}

/**
 * For the pages that are meaningless without a company. `COMPANY_OPTIONAL`
 * keeps them off the screen until one exists, so this throw is a guard against
 * a future routing mistake rather than something a user can reach.
 */
export function useCompany(): Company {
  const { company } = useWorkspace();
  if (!company) throw new Error('شرکتی انتخاب نشده است.');
  return company;
}

const ROUTE_PATHS: Record<RouteId, string> = {
  dashboard: '/',
  companies: '/companies',
  processing: '/processing',
  statements: '/statements',
  ai: '/ai',
  reports: '/reports',
  settings: '/settings',
};

const NAV_ITEMS: Array<{ id: RouteId; label: string; icon: typeof SquaresFour }> = [
  { id: 'dashboard', label: 'مرکز فرمان مالی', icon: SquaresFour },
  { id: 'companies', label: 'شرکت‌ها', icon: Buildings },
  { id: 'processing', label: 'پردازش اطلاعات', icon: Stack },
  { id: 'statements', label: 'صورت‌های مالی', icon: ChartDonut },
  { id: 'ai', label: 'مرکز هوش مالی', icon: Robot },
  { id: 'reports', label: 'گزارش‌های تحلیلی', icon: FileText },
  { id: 'settings', label: 'مدیریت و تنظیمات', icon: GearSix },
];

const PAGES: Record<RouteId, () => JSX.Element> = {
  dashboard: DashboardPage,
  companies: CompaniesPage,
  processing: ProcessingPage,
  statements: StatementsPage,
  ai: AiCenterPage,
  reports: ReportsPage,
  settings: SettingsPage,
};

/**
 * The two routes that must work before any company exists.
 *
 * Without this the first-run organisation was a dead end: every route fell back
 * to the "register your first company" empty state — including the companies
 * page its own button pointed at — so the first company could never be created
 * and settings, and with it the password screen, could never be opened.
 */
const COMPANY_OPTIONAL: ReadonlySet<RouteId> = new Set<RouteId>(['companies', 'settings']);

function routeFromPath(pathname: string): RouteId {
  const found = (Object.entries(ROUTE_PATHS) as Array<[RouteId, string]>).find(([, path]) => path === pathname);
  return found?.[0] ?? 'dashboard';
}

/**
 * Two scopes, deliberately separate: `settings.theme` is the organisation
 * default that a fresh device inherits, and this key is the override for the
 * browser in front of you. The topbar toggle writes the override, so a viewer
 * without `settings.write` can still switch themes without changing what
 * everyone else sees.
 */
const THEME_KEY = 'finojan.theme';

function storedTheme(): Theme | null {
  try {
    const raw = window.localStorage.getItem(THEME_KEY);
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : null;
  } catch {
    // Private-mode storage can throw; the org default is a fine fallback.
    return null;
  }
}

export function App() {
  const [phase, setPhase] = useState<'loading' | 'login' | 'ready'>('loading');
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [companyId, setCompanyId] = useState(() => sessionStorage.getItem('finojan.company') ?? '');
  const [fiscalYearId, setFiscalYearId] = useState(() => sessionStorage.getItem('finojan.fiscalYear') ?? '');
  const [periodKey, setPeriodKey] = useState(() => sessionStorage.getItem('finojan.period') ?? 'full');
  const [periods, setPeriods] = useState<FiscalPeriod[]>([]);
  const [currency, setCurrencyState] = useState<Currency>('rial');
  const [theme, setThemeState] = useState<Theme>(() => storedTheme() ?? 'system');
  const [systemDark, setSystemDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [route, setRoute] = useState<RouteId>(() => routeFromPath(window.location.pathname));
  const [mobileNav, setMobileNav] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await api.bootstrap();
      setBootstrap(next);
      setCurrencyState((sessionStorage.getItem('finojan.currency') as Currency | null) ?? next.settings.displayCurrency);
      // A device override outranks the organisation default; without one the
      // account's setting is what this browser follows.
      setThemeState(storedTheme() ?? next.settings.theme);
      setPhase('ready');
    } catch (error) {
      // Any failure to bootstrap lands on the login screen; there is nothing
      // useful to show without an identity, and a blank shell reads as broken.
      if (!(error instanceof ApiError) || error.status !== 401) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
      setPhase('login');
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // `resolvedTheme` is state-derived rather than read from matchMedia at paint
  // time, so the toggle's icon and the page agree even while «هماهنگ با سیستم»
  // is in force and the OS flips underneath it.
  const resolvedTheme: 'light' | 'dark' = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  useEffect(() => { document.documentElement.dataset.theme = resolvedTheme; }, [resolvedTheme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  const chooseTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try { window.localStorage.setItem(THEME_KEY, next); } catch { /* storage is optional */ }
  }, []);

  useEffect(() => {
    const listener = () => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener('popstate', listener);
    return () => window.removeEventListener('popstate', listener);
  }, []);

  const companies = bootstrap?.companies ?? [];
  const company = companies.find((item) => item.id === companyId) ?? companies[0];
  const fiscalYears = company?.fiscalYears ?? [];
  const fiscalYear = fiscalYears.find((year) => year.id === fiscalYearId) ?? fiscalYears[0] ?? null;

  // The selected year has to belong to the selected company, or the whole app
  // would quietly report one company's figures under another's name.
  useEffect(() => {
    if (fiscalYear && fiscalYear.id !== fiscalYearId) {
      setFiscalYearId(fiscalYear.id);
      sessionStorage.setItem('finojan.fiscalYear', fiscalYear.id);
    }
  }, [fiscalYear, fiscalYearId]);

  useEffect(() => {
    if (!company || !fiscalYear) { setPeriods([]); return undefined; }
    let cancelled = false;
    void api.periods(company.id, fiscalYear.id)
      .then((result) => {
        if (cancelled) return;
        setPeriods(result.periods);
        // A period key belongs to the selected year. Keep it only when the new
        // scope actually has it; otherwise the browser would ask a new company
        // for an old company's period and surface «دوره شناخته نشد».
        setPeriodKey((current) => {
          if (result.periods.some((period) => period.key === current)) return current;
          sessionStorage.setItem('finojan.period', 'full');
          return 'full';
        });
      })
      .catch(() => { if (!cancelled) setPeriods([]); });
    return () => { cancelled = true; };
  }, [company, fiscalYear]);

  const navigate = useCallback((next: RouteId, query: Record<string, string> = {}) => {
    const params = new URLSearchParams(query);
    const suffix = params.toString();
    window.history.pushState({}, '', `${ROUTE_PATHS[next]}${suffix ? `?${suffix}` : ''}`);
    setRoute(next);
    setMobileNav(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const selectCompany = useCallback((next: string) => {
    setCompanyId(next);
    sessionStorage.setItem('finojan.company', next);
    // The previous year id belongs to the previous company; clearing it makes
    // the next render fall through to that company's own latest year.
    setFiscalYearId('');
    sessionStorage.removeItem('finojan.fiscalYear');
    setPeriodKey('full');
    sessionStorage.setItem('finojan.period', 'full');
  }, []);

  const selectFiscalYear = useCallback((next: string) => {
    setFiscalYearId(next);
    sessionStorage.setItem('finojan.fiscalYear', next);
    setPeriodKey('full');
    sessionStorage.setItem('finojan.period', 'full');
  }, []);

  const selectPeriod = useCallback((next: string) => {
    setPeriodKey(next);
    sessionStorage.setItem('finojan.period', next);
  }, []);

  const setCurrency = useCallback((next: Currency) => {
    setCurrencyState(next);
    sessionStorage.setItem('finojan.currency', next);
  }, []);

  const context = useMemo<WorkspaceContextValue | null>(() => (
    bootstrap
      ? {
        bootstrap,
        companies,
        company: company ?? null,
        fiscalYears,
        fiscalYear,
        periods,
        periodKey,
        currency,
        can: (permission: string) => bootstrap.permissions.includes(permission),
        selectCompany,
        selectFiscalYear,
        selectPeriod,
        setCurrency,
        refresh,
        navigate,
      }
      : null
  ), [bootstrap, companies, company, fiscalYears, fiscalYear, periods, periodKey, currency, selectCompany, selectFiscalYear, selectPeriod, setCurrency, refresh, navigate]);

  if (phase === 'loading') {
    return <div className="full-state"><Brand /><LoadingState label="فینوژان در حال آماده‌سازی فضای مالی است" /></div>;
  }
  if (phase === 'login' || !bootstrap) return <LoginPage onAuthenticated={refresh} />;

  // A temporary password gets no further than this. The flag was already being
  // set and returned; nothing read it, so "you will be asked to change it at
  // first login" was a promise the product never kept.
  if (bootstrap.actor.mustChangePassword) {
    return (
      <ChangePasswordPage
        // The server revokes every session for the account on success, so the
        // only honest next screen is the sign-in one.
        onChanged={() => setPhase('login')}
        onSignOut={async () => { await api.logout(); setPhase('login'); }}
      />
    );
  }

  const CurrentPage = PAGES[route];

  return (
    <WorkspaceContext.Provider value={context}>
      <div className="app-shell">
        <aside className={`sidebar ${mobileNav ? 'sidebar--open' : ''}`}>
          <div className="sidebar__head">
            <Brand inverted />
            <button className="icon-button sidebar__close" onClick={() => setMobileNav(false)} aria-label="بستن منو"><X /></button>
          </div>
          <nav className="nav" aria-label="مسیرهای اصلی">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className={route === item.id ? 'nav__item nav__item--active' : 'nav__item'}
                  onClick={() => navigate(item.id)}
                  aria-current={route === item.id ? 'page' : undefined}
                >
                  <Icon size={21} weight={route === item.id ? 'fill' : 'regular'} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          <button className="sidebar__user" onClick={() => navigate('settings')}>
            <span className="avatar">{(bootstrap.actor.displayName || bootstrap.actor.username).slice(0, 1)}</span>
            <span>
              <strong>{bootstrap.actor.displayName || bootstrap.actor.username}</strong>
              <small>{bootstrap.actor.roleLabelFa}</small>
            </span>
            <CaretDown size={15} />
          </button>
        </aside>

        {mobileNav && <button className="sidebar-scrim" onClick={() => setMobileNav(false)} aria-label="بستن منو" />}

        <main className="main-shell">
          <header className="topbar">
            <button className="icon-button mobile-menu" onClick={() => setMobileNav(true)} aria-label="باز کردن منو"><List /></button>

            {company && (
              <div className="scope-bar">
                <span className="scope-bar__item">
                  <Buildings size={17} />
                  <CustomSelect
                    ariaLabel="شرکت فعال"
                    value={company.id}
                    onChange={selectCompany}
                    options={companies.filter((item) => item.status === 'active').map((item) => ({ value: item.id, label: item.name }))}
                  />
                </span>
                {fiscalYear && (
                  <span className="scope-bar__item">
                    <CalendarBlank size={17} />
                    <CustomSelect
                      ariaLabel="سال مالی"
                      value={fiscalYear.id}
                      onChange={selectFiscalYear}
                      options={fiscalYears.map((year) => ({
                        value: year.id,
                        label: year.labelFa,
                        description: year.status === 'closed' ? 'بسته‌شده' : undefined,
                      }))}
                    />
                  </span>
                )}
                {periods.length > 0 && (
                  <span className="scope-bar__item">
                    <CustomSelect
                      ariaLabel="دوره مالی"
                      value={periodKey}
                      onChange={selectPeriod}
                      options={periods.map((period) => ({ value: period.key, label: period.labelFa }))}
                    />
                  </span>
                )}
              </div>
            )}

            <div className="topbar__spacer" />

            <div className="currency-toggle" role="group" aria-label="واحد نمایش مبلغ">
              <CurrencyCircleDollar size={18} />
              {(['toman', 'rial'] as const).map((unit) => (
                <button key={unit} className={currency === unit ? 'active' : ''} onClick={() => setCurrency(unit)}>
                  {unit === 'toman' ? 'تومان' : 'ریال'}
                </button>
              ))}
            </div>

            {/* Flip from what is actually on screen. Toggling off `system`
                compared the literal value, so on a dark OS the first click
                chose «dark» — the theme it was already showing — and looked
                like a dead button. */}
            <button
              className="icon-button"
              onClick={() => chooseTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              aria-label={resolvedTheme === 'dark' ? 'حالت روشن' : 'حالت تیره'}
            >
              {resolvedTheme === 'dark' ? <Sun /> : <Moon />}
            </button>

            <button
              className="icon-button"
              onClick={async () => { await api.logout(); setPhase('login'); }}
              aria-label="خروج"
            >
              <SignOut />
            </button>
          </header>

          <div className="page-wrap">
            {company || COMPANY_OPTIONAL.has(route) ? (
              // The company/year is a security boundary for page state. A key
              // change unmounts every in-flight company-scoped effect before
              // the next company's name is painted, so a late response cannot
              // put the previous company's figures under the new heading.
              <CurrentPage key={`${route}:${company?.id ?? 'none'}:${fiscalYear?.id ?? 'none'}`} />
            ) : (
              <EmptyState
                title="هنوز شرکتی ثبت نشده است"
                description="برای شروع، اولین شرکت را با شناسه ملی و سال مالی آن ثبت کنید تا بتوانید داده‌ها را بارگذاری کنید."
                action={<button className="button button--primary" onClick={() => navigate('companies')}>ثبت شرکت</button>}
              />
            )}
          </div>
        </main>
      </div>
    </WorkspaceContext.Provider>
  );
}
